/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AcknowledgeAnomalyRequest } from '../models/AcknowledgeAnomalyRequest';
import type { AggregatedGreeks } from '../models/AggregatedGreeks';
import type { PortfolioSummary } from '../models/PortfolioSummary';
import type { Position } from '../models/Position';
import type { PositionAnomalySchema } from '../models/PositionAnomalySchema';
import type { PositionsResponse } from '../models/PositionsResponse';
import type { PositionsResponseV2 } from '../models/PositionsResponseV2';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class PositionsService {
    /**
     * Get Positions
     * Get all positions with Greeks and portfolio summary.
     *
     * Returns positions, aggregated Greeks, and portfolio summary metrics.
     * Requires authentication.
     * @returns PositionsResponse Successful Response
     * @throws ApiError
     */
    public static getPositionsApiV1PositionsGet({
        refresh = false,
        symbol,
        positionType,
    }: {
        /**
         * Force refresh from database
         */
        refresh?: boolean,
        /**
         * Filter by symbol/underlying
         */
        symbol?: (string | null),
        /**
         * Filter by type: stock, call, put, all
         */
        positionType?: (string | null),
    }): CancelablePromise<PositionsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/positions',
            query: {
                'refresh': refresh,
                'symbol': symbol,
                'position_type': positionType,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Portfolio Summary
     * Get portfolio summary metrics only (lightweight endpoint).
     *
     * Returns totals without individual positions.
     * @returns PortfolioSummary Successful Response
     * @throws ApiError
     */
    public static getPortfolioSummaryApiV1PositionsSummaryGet({
        refresh = false,
    }: {
        /**
         * Force refresh from database
         */
        refresh?: boolean,
    }): CancelablePromise<PortfolioSummary> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/positions/summary',
            query: {
                'refresh': refresh,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Portfolio Greeks
     * Get aggregated portfolio Greeks.
     *
     * Returns delta, gamma, theta, vega breakdown by underlying.
     * @returns AggregatedGreeks Successful Response
     * @throws ApiError
     */
    public static getPortfolioGreeksApiV1PositionsGreeksGet({
        refresh = false,
    }: {
        /**
         * Force refresh from database
         */
        refresh?: boolean,
    }): CancelablePromise<AggregatedGreeks> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/positions/greeks',
            query: {
                'refresh': refresh,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Sync Positions
     * Sync positions from Google Drive (Fidelity CSV export).
     *
     * Pulls the latest Cursor_Sync_data.csv from Google Drive and updates
     * the database with fresh position data.
     * @returns any Successful Response
     * @throws ApiError
     */
    public static syncPositionsApiV1PositionsSyncPost(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/positions/sync',
        });
    }
    /**
     * Get Positions V2
     * Get all positions with staleness and anomaly information (V2).
     *
     * This endpoint uses the CentralizedPositionService which:
     * - Prioritizes CSV sources (always wins)
     * - Provides market calendar-aware staleness detection
     * - Detects anomalies (60%+ portfolio swings, etc.)
     * - Warns but doesn't block on critical issues
     *
     * New response fields:
     * - staleness: List of staleness info for each data source
     * - anomalies: List of detected anomalies
     * - unacknowledged_critical_count: Count of critical anomalies needing attention
     * @returns PositionsResponseV2 Successful Response
     * @throws ApiError
     */
    public static getPositionsV2ApiV1PositionsV2Get({
        refresh = false,
        includeAnomalyCheck = true,
        includeIv = false,
    }: {
        /**
         * Force refresh from database
         */
        refresh?: boolean,
        /**
         * Include anomaly detection
         */
        includeAnomalyCheck?: boolean,
        /**
         * Include ORATS IV signals per position
         */
        includeIv?: boolean,
    }): CancelablePromise<PositionsResponseV2> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/positions/v2',
            query: {
                'refresh': refresh,
                'include_anomaly_check': includeAnomalyCheck,
                'include_iv': includeIv,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Anomalies
     * Get position anomalies.
     *
     * Returns a list of detected anomalies that can be filtered by severity
     * and acknowledgment status.
     * @returns PositionAnomalySchema Successful Response
     * @throws ApiError
     */
    public static getAnomaliesApiV1PositionsAnomaliesGet({
        severity,
        unacknowledgedOnly = true,
        limit = 50,
    }: {
        /**
         * Filter by severity: CRITICAL, WARNING, INFO
         */
        severity?: ('CRITICAL' | 'WARNING' | 'INFO' | null),
        /**
         * Only return unacknowledged anomalies
         */
        unacknowledgedOnly?: boolean,
        /**
         * Maximum number of results
         */
        limit?: number,
    }): CancelablePromise<Array<PositionAnomalySchema>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/positions/anomalies',
            query: {
                'severity': severity,
                'unacknowledged_only': unacknowledgedOnly,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Acknowledge Anomaly
     * Acknowledge a position anomaly.
     *
     * Marks an anomaly as acknowledged with an optional explanation.
     * Can also mark as false positive to improve future detection.
     * @returns any Successful Response
     * @throws ApiError
     */
    public static acknowledgeAnomalyApiV1PositionsAnomaliesAnomalyIdAcknowledgePost({
        anomalyId,
        requestBody,
    }: {
        anomalyId: number,
        requestBody: AcknowledgeAnomalyRequest,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/positions/anomalies/{anomaly_id}/acknowledge',
            path: {
                'anomaly_id': anomalyId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Position
     * Get a single position by ID.
     *
     * NOTE: This route must be defined LAST to avoid catching
     * paths like /v2, /anomalies, etc. as position IDs.
     * @returns Position Successful Response
     * @throws ApiError
     */
    public static getPositionApiV1PositionsPositionIdGet({
        positionId,
    }: {
        positionId: number,
    }): CancelablePromise<Position> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/positions/{position_id}',
            path: {
                'position_id': positionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
